/**
 * Single Source of Truth für die Passwort-Regeln.
 *
 * WICHTIG: Diese Werte MÜSSEN mit der Cognito `passwordPolicy` in
 * `apps/backend/lib/auth-stack.ts` übereinstimmen. Weicht das Frontend ab,
 * läuft der Nutzer in eine `InvalidPasswordException`, ohne vorher einen
 * Hinweis gesehen zu haben — genau der Bug, den diese Datei verhindert.
 *
 * Bewusste Entscheidung (NIST SP 800-63B): KEINE Zeichenklassen-Regeln
 * (Groß-/Kleinschreibung, Ziffern, Sonderzeichen). Stattdessen eine
 * ausreichende Mindestlänge plus eine kleine Blockliste offensichtlich
 * trivialer Passwörter.
 */
export const PASSWORD_POLICY = {
    /** Cognito: `minLength` */
    minLength: 8,
    /** Cognito-Hardlimit für Passwörter */
    maxLength: 256,
};
/**
 * Offensichtlich triviale Passwörter. Bewusst klein gehalten: die Liste ersetzt
 * keine Breach-Datenbank, fängt aber die Muster ab, die durch den Wegfall der
 * Zeichenklassen-Regeln überhaupt erst möglich wurden ("12345678", "passwort").
 * Vergleich erfolgt case-insensitive.
 */
const COMMON_PASSWORDS = new Set([
    '12345678',
    '123456789',
    '1234567890',
    '87654321',
    'password',
    'password1',
    'password123',
    'passwort',
    'passwort1',
    'passwort123',
    'qwertzuiop',
    'qwertyuiop',
    'iloveyou',
    'sunshine',
    'princess',
    'football',
    'baseball',
    'superman',
    'trustno1',
    'starwars',
    'welcome1',
    'willkommen',
    'admin123',
    'letmein1',
    'abc12345',
    'test1234',
    'atleo123',
]);
/** Ein einzelnes Zeichen wiederholt ("aaaaaaaa") oder reine Zahlenreihe. */
function isTrivialPattern(password) {
    // Kurze Eingaben scheitern bereits an `too_short` — hier würde `includes('')`
    // sonst jeden leeren String als "trivial" markieren.
    if (password.length < 4)
        return false;
    if (/^(.)\1+$/.test(password))
        return true;
    const ascending = '01234567890123456789';
    const descending = '09876543210987654321';
    const lower = password.toLowerCase();
    if (ascending.includes(lower) || descending.includes(lower))
        return true;
    return false;
}
/**
 * Prüft ein Passwort gegen alle Regeln und gibt ALLE Verstöße zurück
 * (nicht nur den ersten) — damit die UI eine vollständige Checkliste zeigen kann.
 */
export function getPasswordIssues(password) {
    const issues = [];
    if (password.length < PASSWORD_POLICY.minLength)
        issues.push('too_short');
    if (password.length > PASSWORD_POLICY.maxLength)
        issues.push('too_long');
    // eslint-disable-next-line security/detect-possible-timing-attacks -- Vergleich gegen die eigene getrimmte Kopie, kein Secret-Abgleich
    if (password !== password.trim())
        issues.push('whitespace_edges');
    if (COMMON_PASSWORDS.has(password.toLowerCase()) || isTrivialPattern(password))
        issues.push('too_common');
    return issues;
}
/** Convenience-Wrapper für Backend-Guards. */
export function isPasswordAcceptable(password) {
    return getPasswordIssues(password).length === 0;
}
